// apps/mtd-itsa-web/src/main.tsx
import { initializeSupabase } from "@kordbooks/supabase-client";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router"; // Corrected import
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/providers/auth-provider";
import { RouteProvider } from "@/providers/router-provider";
import { ThemeProvider } from "@/providers/theme-provider";
import "@/styles/globals.css";
import { AppRoutes } from "@/routes";
import { ProjectProvider } from '@/providers/project-provider';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import ErrorFallback from './components/application/error-fallback/error-fallback';
import { ToastProvider } from "@/components/application/toast";


// Get environment variables - Vite makes these available via import.meta.env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Missing Supabase environment variables");
}

initializeSupabase(supabaseUrl, supabaseAnonKey);

// Initialize Sentry
// We check if DSN exists so it doesn't crash locally if you haven't set the var yet
if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 0.0, // Capture 0% of transactions (adjust for production)
        // Session Replay
        replaysSessionSampleRate: 0.0, // Sample 0% of sessions
        replaysOnErrorSampleRate: 0.05, // Sample 5% of sessions where errors occur
    });
}

// Initialize PostHog
if (import.meta.env.VITE_POSTHOG_KEY) {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,

        // ADD THIS: Automatically tag every event with the environment
        loaded: (ph) => {
            ph.register({
                environment: import.meta.env.VITE_ENV || 'local'
            });
        }
    });
}


// Create a client for React Query
const queryClient = new QueryClient();


createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
                <ErrorFallback
                    title="Something went wrong"
                    error={error}
                    resetError={resetError}
                />
            )}
            showDialog={false}
        >
            <BrowserRouter>
                <QueryClientProvider client={queryClient}>
                    <PostHogProvider client={posthog}>
                        <ThemeProvider>
                            <ToastProvider>
                                <AuthProvider>
                                    <ProjectProvider>
                                        <RouteProvider>
                                            <AppRoutes />
                                        </RouteProvider>
                                    </ProjectProvider>
                                </AuthProvider>
                            </ToastProvider>
                        </ThemeProvider>
                    </PostHogProvider>
                </QueryClientProvider>
            </BrowserRouter>
        </Sentry.ErrorBoundary>
    </StrictMode>
);
